/**
 * Writing Service
 * API calls for the Writing learning journey (formerly Review Quest)
 */

import { apiClient as api } from './apiClient';
import {
  GateType,
  QuestProgress,
  QuestProgressResponse,
  QuestQuestionsResponse,
  QuestEvaluationResponse,
  QuestDiagnosticResponse,
  GateEvaluationResult,
  DiagnosticReport,
  TranslationQuestion,
  CombineQuestion,
  ExpansionQuestion,
  EssayQuestion,
  LeaderboardEntry,
  LeaderboardResponse,
  DailyStatus,
  StreakInfo,
  VocabularySourcesResponse,
  StartStandaloneQuestDto,
  StandaloneQuestProgress,
  GateReviewData,
  UserLearningData,
  VocabularyAnalysis,
  FamiliarityChangeItem,
} from '../types/writing';

/**
 * Get or create writing progress for a course
 */
export async function getQuestProgress(courseId: string): Promise<QuestProgress> {
  const response = await api.get<QuestProgressResponse>(`/writing/${courseId}/progress`);
  return response.data.data;
}

/**
 * Start a new writing session for a course
 */
export async function startQuest(courseId: string): Promise<QuestProgress> {
  const response = await api.post<QuestProgressResponse>(`/writing/${courseId}/start`, {});
  return response.data.data;
}

/**
 * Get questions for a specific gate
 */
export async function getGateQuestions(
  courseId: string,
  gateType: GateType
): Promise<{
  gateType: GateType;
  questions: TranslationQuestion[] | CombineQuestion[] | ExpansionQuestion[] | EssayQuestion[];
}> {
  const response = await api.get<QuestQuestionsResponse>(
    `/writing/${courseId}/gate/${gateType}/questions`
  );
  return response.data.data;
}

/**
 * Submit answer for evaluation
 */
export async function submitGateAnswer(
  courseId: string,
  gateType: GateType,
  questionId: string,
  answer: string
): Promise<GateEvaluationResult> {
  const response = await api.post<QuestEvaluationResponse>(
    `/writing/${courseId}/gate/${gateType}/submit`,
    {
      questionId,
      answer,
    }
  );
  return response.data.data;
}

/**
 * Complete a gate and move to next
 */
export async function completeGate(
  courseId: string,
  gateType: GateType,
  finalScore: number
): Promise<QuestProgress> {
  const response = await api.post<QuestProgressResponse>(
    `/writing/${courseId}/gate/${gateType}/complete`,
    {
      score: finalScore,
    }
  );
  return response.data.data;
}

/**
 * Get diagnostic report after completing all gates
 */
export async function getDiagnosticReport(courseId: string): Promise<DiagnosticReport> {
  const response = await api.get<QuestDiagnosticResponse>(
    `/writing/${courseId}/diagnostic`
  );
  return response.data.data;
}

/**
 * Generate diagnostic report (triggers AI analysis)
 */
export async function generateDiagnosticReport(courseId: string): Promise<DiagnosticReport> {
  const response = await api.post<QuestDiagnosticResponse>(
    `/writing/${courseId}/diagnostic/generate`,
    {}
  );
  return response.data.data;
}

/**
 * Get gate review data for a completed writing session (view results)
 */
export async function getGateReview(
  courseId: string,
  gateType: GateType
): Promise<GateReviewData> {
  const response = await api.get<{ success: boolean; data: GateReviewData }>(
    `/writing/${courseId}/gate/${gateType}/review`
  );
  return response.data.data;
}

/**
 * Retry a course writing session with the same questions
 */
export async function retryCourseQuest(courseId: string): Promise<QuestProgress> {
  const response = await api.post<QuestProgressResponse>(
    `/writing/${courseId}/retry`,
    {}
  );
  return response.data.data;
}

/**
 * Get standalone gate review data for a completed writing session (view results)
 */
export async function getStandaloneGateReview(
  gateType: GateType
): Promise<GateReviewData> {
  const response = await api.get<{ success: boolean; data: GateReviewData }>(
    `/writing/standalone/gate/${gateType}/review`
  );
  return response.data.data;
}

/**
 * Get leaderboard
 */
export async function getLeaderboard(
  period: 'daily' | 'weekly' | 'monthly' | 'all-time' = 'weekly',
  limit = 20
): Promise<{
  entries: LeaderboardEntry[];
  userRank?: LeaderboardEntry;
  period: string;
}> {
  const response = await api.get<LeaderboardResponse>(
    `/writing/leaderboard?period=${period}&limit=${limit}`
  );
  return response.data.data;
}

/**
 * Get user's writing history
 */
export async function getQuestHistory(
  limit = 10,
  offset = 0
): Promise<{
  quests: QuestProgress[];
  total: number;
}> {
  const response = await api.get<{
    success: boolean;
    data: { quests: QuestProgress[]; total: number };
  }>(`/writing/history?limit=${limit}&offset=${offset}`);
  return response.data.data;
}

/**
 * Get user's total XP and badges
 */
export async function getUserQuestStats(): Promise<{
  totalXp: number;
  questsCompleted: number;
  averageScore: number;
  badges: string[];
  currentStreak: number;
}> {
  const response = await api.get<{
    success: boolean;
    data: {
      totalXp: number;
      questsCompleted: number;
      averageScore: number;
      badges: string[];
      currentStreak: number;
    };
  }>('/writing/stats');
  return response.data.data;
}

// ============================================
// STANDALONE WRITING API FUNCTIONS
// ============================================

/**
 * Get daily challenge status
 */
export async function getDailyStatus(): Promise<DailyStatus> {
  const response = await api.get<{ success: boolean; data: DailyStatus }>(
    '/writing/daily/status'
  );
  return response.data.data;
}

/**
 * Get user streak information
 */
export async function getStreakInfo(): Promise<StreakInfo> {
  const response = await api.get<{ success: boolean; data: StreakInfo }>(
    '/writing/streak'
  );
  return response.data.data;
}

/**
 * Get vocabulary source options with counts
 */
export async function getVocabularySources(): Promise<VocabularySourcesResponse> {
  const response = await api.get<{ success: boolean; data: VocabularySourcesResponse }>(
    '/writing/vocabulary-sources'
  );
  return response.data.data;
}

/**
 * Start a standalone writing session (not bound to course)
 */
export async function startStandaloneQuest(
  dto: StartStandaloneQuestDto
): Promise<StandaloneQuestProgress> {
  const response = await api.post<{ success: boolean; data: StandaloneQuestProgress }>(
    '/writing/standalone/start',
    dto
  );
  return response.data.data;
}

/**
 * Get current standalone writing progress
 */
export async function getStandaloneProgress(): Promise<StandaloneQuestProgress> {
  const response = await api.get<{ success: boolean; data: StandaloneQuestProgress }>(
    '/writing/standalone/progress'
  );
  return response.data.data;
}

/**
 * Get questions for a standalone writing gate
 */
export async function getStandaloneGateQuestions(
  gateType: GateType
): Promise<{
  gateType: GateType;
  questions: TranslationQuestion[] | CombineQuestion[] | ExpansionQuestion[] | EssayQuestion[];
}> {
  const response = await api.get<QuestQuestionsResponse>(
    `/writing/standalone/gate/${gateType}/questions`
  );
  return response.data.data;
}

/**
 * Submit answer for a standalone writing gate
 */
export async function submitStandaloneGateAnswer(
  gateType: GateType,
  questionId: string,
  answer: string
): Promise<GateEvaluationResult> {
  const response = await api.post<QuestEvaluationResponse>(
    `/writing/standalone/gate/${gateType}/submit`,
    { questionId, answer }
  );
  return response.data.data;
}

/**
 * Complete a standalone writing gate
 */
export async function completeStandaloneGate(
  gateType: GateType,
  finalScore: number
): Promise<StandaloneQuestProgress> {
  const response = await api.post<{ success: boolean; data: StandaloneQuestProgress }>(
    `/writing/standalone/gate/${gateType}/complete`,
    { score: finalScore }
  );
  return response.data.data;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get gate display name
 */
export function getGateName(gateType: GateType, lang = 'zh-TW'): string {
  const names: Record<GateType, Record<string, string>> = {
    translation: { 'zh-TW': '翻譯', en: 'Translation' },
    combine: { 'zh-TW': '造句', en: 'Sentence Combining' },
    expansion: { 'zh-TW': '擴寫', en: 'Paragraph Expansion' },
    essay: { 'zh-TW': '作文', en: 'Essay Writing' },
  };
  return names[gateType]?.[lang] || names[gateType]?.en || gateType;
}

/**
 * Get gate icon
 */
export function getGateIcon(gateType: GateType): string {
  const icons: Record<GateType, string> = {
    translation: '📝',
    combine: '🔗',
    expansion: '📖',
    essay: '✍️',
  };
  return icons[gateType] || '📋';
}

/**
 * Get token cost for a gate
 */
export function getGateTokenCost(gateType: GateType): number {
  const costs: Record<GateType, number> = {
    translation: 1,
    combine: 2,
    expansion: 3,
    essay: 5,
  };
  return costs[gateType] || 1;
}

/**
 * Get XP reward for passing a gate
 */
export function getGateXpReward(gateType: GateType): number {
  const rewards: Record<GateType, number> = {
    translation: 10,
    combine: 20,
    expansion: 30,
    essay: 50,
  };
  return rewards[gateType] || 10;
}

/**
 * Calculate total token cost for all gates
 */
export function getTotalTokenCost(): number {
  return 1 + 2 + 3 + 5; // 11 tokens total
}

/**
 * Calculate total possible XP
 */
export function getTotalPossibleXp(): number {
  return 10 + 20 + 30 + 50; // 110 XP total
}

/**
 * Format score with color indicator
 */
export function getScoreClass(score: number): string {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
}

/**
 * Check if gate is accessible
 */
export function isGateAccessible(
  gateType: GateType,
  progress: QuestProgress
): boolean {
  const gateOrder: GateType[] = ['translation', 'combine', 'expansion', 'essay'];
  const currentIndex = gateOrder.indexOf(gateType);

  if (currentIndex === 0) return true;

  const previousGate = gateOrder[currentIndex - 1];
  return progress.gateProgress[previousGate] === 'completed';
}

// ============================================
// USER LEARNING DATA & VOCABULARY ANALYSIS
// ============================================

/**
 * Get user's learning data (vocabulary, knowledge points, ability level)
 * Should be called when entering writing pages
 */
export async function getUserLearningData(): Promise<UserLearningData> {
  const response = await api.get<{ success: boolean; data: UserLearningData }>(
    '/writing/user-learning-data'
  );
  return response.data.data;
}

/**
 * Analyze vocabulary usage in an answer
 * Called after essay/expansion submission
 */
export async function analyzeVocabularyUsage(
  answer: string,
  requiredVocabulary: string[],
  userLearningData: UserLearningData,
  gateType: GateType
): Promise<VocabularyAnalysis> {
  const response = await api.post<{ success: boolean; data: VocabularyAnalysis }>(
    '/writing/analyze-vocabulary',
    {
      answer,
      requiredVocabulary,
      userVocabulary: userLearningData.vocabulary,
      gateType,
    }
  );
  return response.data.data;
}

/**
 * Update familiarity scores in database
 * Called after familiarity animation is shown
 */
export async function updateFamiliarity(
  changes: FamiliarityChangeItem[]
): Promise<void> {
  await api.post('/vocabulary/vocabulary-book/update-familiarity', {
    changes: changes.map(c => ({
      wordId: c.wordId,
      word: c.word,
      change: c.change,
      reason: c.reason,
      isNew: c.isNew,
    })),
  });
}
